import removeSpreadReducer from 'editor/src/store/design/reducer/spreadManipulation/removeSpreadReducer';
import { removeSpreadAction } from 'editor/src/store/design/slice';
import { DesignData, DesignState, Spread } from 'editor/src/store/design/types';

export interface SpreadRemovalPlan {
  // Indexes to dispatch removeSpreadAction with, in order; each one points into the spreads left
  // after the previous removal.
  removeIndexes: number[];
  removedSpreads: Spread[];
}

// What removing `spreadCount` spreads at `spreadIndex` takes away. Worked out on a copy through
// the removal reducer itself, because every removal re-runs the page math and moves the canRemove
// flags the next step reads. A target that cannot be removed yields an empty plan: the step
// starts at the clicked spread, so nothing else may go in its place.
function planSpreadRemoval(designData: DesignData, spreadIndex: number, spreadCount: number): SpreadRemovalPlan {
  if (!designData.spreads[spreadIndex]?.canRemove) {
    return { removeIndexes: [], removedSpreads: [] };
  }

  const draft = {
    designData: {
      ...designData,
      spreads: designData.spreads.map((spread) => ({ ...spread, pages: spread.pages.map((page) => ({ ...page })) })),
    },
  } as DesignState;
  const remaining = [...designData.spreads];
  const plan: SpreadRemovalPlan = { removeIndexes: [], removedSpreads: [] };

  for (let i = 0; i < spreadCount; i += 1) {
    const spreads = draft.designData?.spreads ?? [];
    if (spreads.length <= 1) {
      break;
    }
    // Removing a full grid step must not stop short — an off-grid count would make the product
    // revert to its minimum. Clamp to the shrinking array (the clicked spread can sit within one
    // increment of the end) and walk back past non-removable spreads (the page math pins the last
    // inside spread of a spread book).
    let removeIndex = Math.min(spreadIndex, spreads.length - 1);
    while (removeIndex >= 0 && !spreads[removeIndex]?.canRemove) {
      removeIndex -= 1;
    }
    if (removeIndex < 0) {
      break;
    }

    removeSpreadReducer(draft, removeSpreadAction(removeIndex));
    plan.removeIndexes.push(removeIndex);
    plan.removedSpreads.push(...remaining.splice(removeIndex, 1));
  }

  return plan;
}

export default planSpreadRemoval;
